import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Shield, AlertTriangle, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

const OwnerSetupPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAuth } = useAuth();
  const [ownerExists, setOwnerExists] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimSecret, setClaimSecret] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    checkOwnerStatus();
  }, []);

  const checkOwnerStatus = async () => {
    try {
      const response = await axios.get(`${API}/internal/owner-status`);
      setOwnerExists(response.data.owner_exists);
    } catch (err) {
      console.error("Error checking owner status:", err);
      setOwnerExists(true); // Assume exists if error
    } finally {
      setLoading(false);
    }
  };

  const handleClaimOwnership = async (e) => {
    e.preventDefault();
    setError("");
    setClaiming(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/internal/claim-owner`,
        { claim_secret: claimSecret },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Ownership claimed successfully!");
      await checkAuth(); // Refresh user data
      
      // Show success briefly then redirect
      setTimeout(() => {
        navigate("/admin");
      }, 2000);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to claim ownership";
      setError(message);
      toast.error(message);
    } finally {
      setClaiming(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Owner already exists - show 404-like page
  if (ownerExists) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Page Not Found</CardTitle>
            <CardDescription>
              This page does not exist or is no longer available.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/")} variant="outline">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl">Authentication Required</CardTitle>
            <CardDescription>
              You must be logged in to access this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/auth")} className="bg-slate-900 hover:bg-slate-800">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Owner claim form
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Platform Ownership Setup</CardTitle>
          <CardDescription>
            Claim ownership of this ReadMaster instance.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Warning:</strong> This action can only be performed <strong>once</strong>. 
              After claiming ownership, this endpoint will be permanently disabled.
            </AlertDescription>
          </Alert>

          <div className="bg-slate-100 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-600 mb-2">Logged in as:</p>
            <p className="font-medium text-slate-900">{user?.email}</p>
            <p className="text-sm text-slate-500">Current role: {user?.role || "USER"}</p>
          </div>

          <form onSubmit={handleClaimOwnership} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Owner Claim Secret
              </label>
              <Input
                type="password"
                placeholder="Enter the OWNER_CLAIM_SECRET"
                value={claimSecret}
                onChange={(e) => setClaimSecret(e.target.value)}
                required
                className="font-mono"
                data-testid="claim-secret-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                This secret is provided in the server environment configuration.
              </p>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={claiming || !claimSecret}
              data-testid="claim-owner-btn"
            >
              {claiming ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Claiming Ownership...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Claim Ownership
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-slate-500 text-center mt-6">
            By claiming ownership, you will have full administrative control over this platform.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default OwnerSetupPage;
